import { Box, ContentBlock } from 'braid-design-system';
import { JobDetailsView } from '@seek/job-details-view';
import { usePropsForJobDetailsView } from './usePropsForJobDetailsView';
import Lmis, { useIsNullLmis } from 'src/components/Lmis/Lmis';
import type { JobDetails } from 'src/store/jobdetails/jobdetails';
import { ScreenReaderSkipTarget } from 'src/components/ScreenReaderSkipTarget/ScreenReaderSkipTarget';
import { selectFeatureFlag } from 'src/store/selectors';
import { useSelector } from 'src/store/react';
import { useEffect, useState } from 'react';
import { SearchResultPage } from 'src/handlers/SearchResults';
import { HomePage } from 'src/handlers/Home';
import { useSplitViewAccessibilityLinks } from '../SearchResultPage/SplitView/AccessabilityLinks/AccessabilityLinks';
import { useSplitViewContext } from 'src/handlers/SplitViewContext';
import { OptionsBarView } from '../OptionsBar/OptionsBarView';

interface JobDetailsPageProps {
  jobDetails: JobDetails;
  embedded?: boolean;
}

const JobDetailsPage = ({ jobDetails, embedded }: JobDetailsPageProps) => {
  const props = usePropsForJobDetailsView(jobDetails);
  const isNullLmis = useIsNullLmis({});

  const { shouldRenderOptionsBar } = useSplitViewContext();

  // State is used here to prevent a hydration error from happening
  // Saved jobs data is not fetched server side, so we can only update the save button client side
  const [isSaved, setIsSaved] = useState(false);
  useEffect(() => {
    if (props.job.isSaved !== undefined) {
      setIsSaved(props.job.isSaved);
    }
  }, [props.job.isSaved]);

  const propsWithIsSaved = {
    ...props,
    job: {
      ...props.job,
      isSaved,
    },
  };

  const lmis = isNullLmis ? undefined : <Lmis />;

  const shouldShowStickyApplyOnDesktop = useSelector(
    selectFeatureFlag('showStickyApplyOnDesktop'),
  );

  const salaryTransparencyEnabled = useSelector(
    selectFeatureFlag('salaryTransparency'),
  );

  const isReduceDuplicatedApplyEnabled = useSelector(
    selectFeatureFlag('reducedDuplicatedApply'),
  );

  const { focusReturnToSearchLink } = useSplitViewAccessibilityLinks();
  useEffect(() => {
    focusReturnToSearchLink();
  }, [focusReturnToSearchLink]);

  const content = (
    <>
      {shouldRenderOptionsBar ? (
        <OptionsBarView jobDetail={jobDetails} />
      ) : null}
      <JobDetailsView
        {...propsWithIsSaved}
        careerInsights={lmis}
        hideJobLink={propsWithIsSaved.jobUrlOpts?.hide}
        embedded={embedded}
        stickApplyButtonOnDesktop={shouldShowStickyApplyOnDesktop}
        salaryTransparencyEnabled={salaryTransparencyEnabled}
        featureFlags={{
          enableReducedDuplicatedApply: isReduceDuplicatedApplyEnabled,
        }}
      />
    </>
  );

  useEffect(() => {
    HomePage.preload();
    SearchResultPage.preload();
  }, []);

  return embedded ? (
    <Box
      // Prevent share button from being cropped by scroll container
      // (only an issue when the job has no cover image or logo)
      paddingTop={
        props.job.branding?.assets.cover?.url ||
        props.job.branding?.assets.logo?.url ||
        shouldRenderOptionsBar
          ? undefined
          : 'xxsmall'
      }
      data={{ automation: 'jobDetailsPage' }}
    >
      {content}
    </Box>
  ) : (
    <Box
      // Provide space from header divider on desktop
      paddingTop={{ desktop: 'large' }}
      data={{ automation: 'jobDetailsPage' }}
    >
      <ContentBlock width="medium">
        <ScreenReaderSkipTarget name="start-of-content" />
        {content}
      </ContentBlock>
    </Box>
  );
};

export default JobDetailsPage;
